import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddStudentForm from '../components/AddStudentForm';
import ClassSummary from '../components/ClassSummary';
import PasukPicker from '../components/PasukPicker';
import StudentsTable from '../components/StudentsTable';
import Card from '../design/Card';
import EmptyState from '../design/EmptyState';
import ErrorState from '../design/ErrorState';
import LoadingState from '../design/LoadingState';
import PageHeader from '../design/PageHeader';
import Select from '../design/Select';
import {
  getClassesForTeacher,
  getProgressForClass,
  getStudentsInClass,
  setClassCurrentPasuk,
} from '../utils/schoolFirestore';
import { formatPasukRef, pasukKey } from '../utils/pasuk';
import type { PasukRef, SchoolClass, Student, StudentProgress, Teacher } from '../types';

interface TeacherClassPageProps {
  teacher: Teacher;
}

const TeacherClassPage: React.FC<TeacherClassPageProps> = ({ teacher }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [classes, setClasses] = useState<SchoolClass[] | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [progress, setProgress] = useState<Record<string, StudentProgress>>({});
  const [loadingClass, setLoadingClass] = useState(false);
  const [error, setError] = useState(false);

  const selectedClass =
    classes?.find((c) => c.id === searchParams.get('class')) ??
    (classes && classes.length > 0 ? classes[0] : null);

  const loadClasses = useCallback(async (): Promise<void> => {
    setError(false);
    try {
      setClasses(await getClassesForTeacher(teacher));
    } catch (err) {
      console.error('[school] Failed to load classes', err);
      setError(true);
    }
  }, [teacher]);

  const loadClassData = useCallback(async (schoolClass: SchoolClass): Promise<void> => {
    setLoadingClass(true);
    setError(false);
    try {
      const [s, p] = await Promise.all([
        getStudentsInClass(schoolClass.schoolId, schoolClass.id),
        getProgressForClass(schoolClass.schoolId, schoolClass.id),
      ]);
      setStudents(s);
      setProgress(p);
    } catch (err) {
      console.error('[school] Failed to load class data', err);
      setError(true);
    } finally {
      setLoadingClass(false);
    }
  }, []);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const selectedId = selectedClass?.id;
  useEffect(() => {
    if (selectedClass) loadClassData(selectedClass);
    // Reload only when the selected class changes, not on every classes refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, loadClassData]);

  const saveCurrentPasuk = async (pasuk: PasukRef): Promise<void> => {
    if (!selectedClass) return;
    await setClassCurrentPasuk(selectedClass.id, pasuk);
    setClasses((prev) =>
      prev
        ? prev.map((c) => (c.id === selectedClass.id ? { ...c, currentPasuk: pasuk } : c))
        : prev,
    );
  };

  if (error && !classes) {
    return <ErrorState onRetry={loadClasses} />;
  }
  if (!classes) return <LoadingState />;
  if (!selectedClass) {
    return (
      <EmptyState
        icon="users"
        title={t('school_no_classes')}
        description={t('school_no_classes_hint')}
      />
    );
  }

  const currentKey = selectedClass.currentPasuk ? pasukKey(selectedClass.currentPasuk) : null;

  return (
    <>
      <PageHeader
        title={t('school_class_progress_title', { className: selectedClass.name })}
        subtitle={
          selectedClass.currentPasuk
            ? t('school_current_pasuk_is', {
                ref: formatPasukRef(selectedClass.currentPasuk, i18n.language),
              })
            : t('school_no_current_pasuk_title')
        }
        actions={
          classes.length > 1 && (
            <Select
              label={t('school_class')}
              value={selectedClass.id}
              options={classes.map((c) => ({ value: c.id, label: c.name }))}
              onChange={(id: string): void => setSearchParams({ class: id })}
              className="min-w-[160px]"
            />
          )
        }
      />

      {error && (
        <div className="mb-6">
          <ErrorState onRetry={(): void => void loadClassData(selectedClass)} />
        </div>
      )}

      {loadingClass ? (
        <LoadingState />
      ) : (
        <div className="flex flex-col gap-6">
          <ClassSummary students={students} progressByStudent={progress} currentKey={currentKey} />

          <div className="grid gap-6 xl:grid-cols-3">
            <Card title={t('school_students')} flush className="min-w-0 xl:col-span-2">
              <StudentsTable
                students={students}
                progressByStudent={progress}
                currentKey={currentKey}
                onSelect={(s: Student): void =>
                  navigate(`/students/${s.id}?class=${selectedClass.id}`)
                }
              />
            </Card>

            <div className="flex flex-col gap-6">
              <Card title={t('school_current_pasuk')}>
                <PasukPicker value={selectedClass.currentPasuk} onSave={saveCurrentPasuk} />
              </Card>
              <Card title={t('school_add_student')}>
                <div className="mb-4 rounded-xl bg-school-primary-soft p-3 text-sm text-school-primary">
                  {t('school_class_code_is')}{' '}
                  <span className="font-mono text-base font-bold" dir="ltr">
                    {selectedClass.joinCode}
                  </span>
                </div>
                <AddStudentForm
                  schoolClass={selectedClass}
                  existing={students}
                  onAdded={(s: Student): void => setStudents((prev) => [...prev, s])}
                />
              </Card>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeacherClassPage;
